import { useEffect, useMemo, useState } from 'react'

import { checkIsLanguageSupported, getCultureFromRegistrationLink } from '@/libs/utils/language'

const ONETRUST_CONFIG = {
  SCRIPT_ID: 'oneTrustLib',
  BUTTON_ID: 'ot-sdk-btn',
  DOMAIN_ID: 'd8f3a9b2-8a80-466f-b82f-6281c84ca3b0',
  SCRIPT_URL: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
  MAX_ATTEMPTS: 3,
  RETRY_DELAY: 1500,
}

const getSafeLocalStorageItem = (key) => {
  if (typeof globalThis.window === 'undefined') return null
  try {
    return localStorage.getItem(key)
  } catch (error) {
    console.warn(`Failed to access localStorage for key: ${key}`, error)
    return null
  }
}

const getSafePathname = () => {
  if (typeof globalThis.window === 'undefined') return ''
  return globalThis.location.pathname
}

function CookieBannerScripts() {
  const [languageInLocalStorage, setLanguageInLocalStorage] = useState(() =>
    getSafeLocalStorageItem('lng')
  )

  useEffect(() => {
    const changeLangHandler = (event) => {
      if (event.detail?.key === 'lng') {
        setLanguageInLocalStorage(event.detail.value)
      }
    }
    document.addEventListener('changeLang', changeLangHandler)

    return () => {
      document.removeEventListener('changeLang', changeLangHandler)
    }
  }, [])

  const languageCode = useMemo(() => {
    if (languageInLocalStorage) {
      return languageInLocalStorage
    }

    const paths = getSafePathname()
    if (!paths) return 'en' // Fallback for SSR

    const languageOnPathName = paths.split('/').find((path) => path)

    // "registration router" has a different format to get the culture
    if (languageOnPathName === 'registration') {
      return getCultureFromRegistrationLink(paths)
    }
    // normal routers
    return checkIsLanguageSupported(languageOnPathName) ? languageOnPathName : 'en'
  }, [languageInLocalStorage])

  const oneTrustLanguage = useMemo(() => {
    if (languageCode === 'de-FT') {
      return 'de-DE'
    }

    return languageCode
  }, [languageCode])

  useEffect(() => {
    let timeoutId
    let scriptElement

    const OptanonWrapper = () => {
      const paths = getSafePathname()

      if (!paths.includes('data-protection-policy')) {
        return
      }

      let attempts = 0

      const checkCookieButton = () => {
        const cookieButton = document.getElementById(ONETRUST_CONFIG.BUTTON_ID)

        if (cookieButton?.textContent) {
          return
        }

        attempts += 1

        if (attempts < ONETRUST_CONFIG.MAX_ATTEMPTS) {
          timeoutId = setTimeout(checkCookieButton, ONETRUST_CONFIG.RETRY_DELAY)
        }
      }

      checkCookieButton()
    }

    const loadOneTrustScript = () => {
      try {
        if (import.meta.env.DEV) {
          console.info('cookie banner disabled in development')
          return
        }

        // Remove existing script if it exists
        const existingScript = document.getElementById(ONETRUST_CONFIG.SCRIPT_ID)
        if (existingScript) {
          existingScript.remove()
        }

        const script = document.createElement('script')
        script.src = ONETRUST_CONFIG.SCRIPT_URL
        script.type = 'text/javascript'
        script.async = true
        script.setAttribute('id', ONETRUST_CONFIG.SCRIPT_ID)
        script.setAttribute('charset', 'UTF-8')
        script.dataset.language = oneTrustLanguage
        script.dataset.domainScript = ONETRUST_CONFIG.DOMAIN_ID

        script.addEventListener('load', OptanonWrapper)
        script.onerror = (error) => {
          console.warn('Failed to load OneTrust script:', error)
        }

        document.body.append(script)
        scriptElement = script
      } catch (error) {
        console.error('Error loading OneTrust script:', error)
      }
    }

    // Only load script in browser environment
    if (typeof globalThis.window !== 'undefined') {
      loadOneTrustScript()
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      if (scriptElement && document.body.contains(scriptElement)) {
        try {
          scriptElement.remove()
        } catch (error) {
          console.warn('Script cleanup warning:', error)
        }
      }
    }
  }, [oneTrustLanguage])

  return null
}

function AdobeAnalyticScripts() {
  useEffect(() => {
    // Safely execute Adobe Analytics pageBottom method
    try {
      if (typeof globalThis.window !== 'undefined' && globalThis._satellite?.pageBottom) {
        globalThis._satellite.pageBottom()
      }
    } catch (error) {
      console.warn('Adobe Analytics pageBottom execution failed:', error)
    }
  }, [])

  return null
}

function ExternalFileScripts() {
  return (
    <>
      <CookieBannerScripts />
      <AdobeAnalyticScripts />
    </>
  )
}

export default ExternalFileScripts
